import { OVERLAY_DEFAULT_CONFIG, OverlayContainer } from '@angular/cdk/overlay'
import { APP_BASE_HREF, PlatformLocation } from '@angular/common'
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
  withXhr,
} from '@angular/common/http'
import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideEnvironmentInitializer,
  provideZoneChangeDetection,
} from '@angular/core'
import { provideSignalFormsConfig } from '@angular/forms/signals'
import { NG_STATUS_CLASSES } from '@angular/forms/signals/compat'
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async'
import { Router } from '@angular/router'

import { EffectsModule } from '@ngrx/effects'
import { StoreRouterConnectingModule, routerReducer } from '@ngrx/router-store'
import { StoreModule } from '@ngrx/store'
import { StoreDevtoolsModule } from '@ngrx/store-devtools'
import { definePreset } from '@primeng/themes'
import Aura from '@primeng/themes/aura'
import * as Sentry from '@sentry/angular'
import { createDomain } from 'effector'
import { MessageService } from 'primeng/api'
import { providePrimeNG } from 'primeng/config'

import { FlxIconModule, provideExtraIconSet } from '@flowx/angular-ui-toolkit'
import { AppRoutingModule } from '@flowx/designer/app-routing.module'
import { environment } from '@flowx/designer/environment'
import { FlxOverlayContainer } from '@flowx/designer/flx-overlay'
import { AiAppIdInterceptor } from '@flowx/designer/modules/ai-builder/ai-app-id.interceptor'
import { ErrorInterceptor } from '@flowx/designer/modules/auth/auth.interceptor'
import { AuthConfigService } from '@flowx/designer/modules/auth/auth.service'
import { MEDIA_LIBRARY_API } from '@flowx/designer/modules/core/dictionary/media-library.dictionary'
import { MonacoEditorModule } from '@flowx/designer/modules/monaco-code-editor/editor.module'
import { ScriptingIcons } from '@flowx/designer/modules/script-builder'
import { WorkspacesInterceptor } from '@flowx/designer/modules/workspaces/workspaces.interceptor'
import { FlxDateFormatPipe, FlxTableDateFormatPipe } from '@flowx/designer/pipes/flx-date.pipe'
import { FlxHighlightPipe } from '@flowx/designer/pipes/highlight.pipe'
import { FlxFileSizePipe } from '@flowx/designer/pipes/size.pipe'
import { EFFECTOR_TOKEN } from '@flowx/designer/shared/angular-effector'
import { MediaLibraryPrivateApi } from '@flowx/designer/shared/api/media-library-private.api'
import { SESSION_LOGOUT } from '@flowx/designer/shared/api/session-logout.token'
import { CMS_ICON_SET } from '@flowx/designer/shared/cms-icons'
import { NotificationsSseService } from '@flowx/designer/shared/components/notifications/services/notifications-sse.service'
import { DESIGNER_ICON_SET } from '@flowx/designer/shared/designer-icons'
import { monacoLoader } from '@flowx/designer/shared/entities/utils'
import { AnalyticsErrorHandler } from '@flowx/designer/shared/services/analytics-error-handler.service'
import { MediaLibraryService } from '@flowx/designer/shared/services/media-library.service'
import { ReusableTemplatesService } from '@flowx/designer/shared/services/reusable-template.service'
import { UiFlowsService } from '@flowx/designer/shared/services/ui-flows.service'
import * as fromStore from '@flowx/designer/shared/store'
import { StableRouterEffects } from '@flowx/designer/shared/store/effects/stable-router.effects'
import {
  dataModelFeatureKey,
  dataModelReducer,
} from '@flowx/designer/shared/store/reducers/data-model.reducer'
import {
  reusableFunctionsFeatureKey,
  reusableFunctionsReducer,
} from '@flowx/designer/shared/store/reducers/reusable-functions.reducer'
import {
  stableRouterFeatureKey,
  stableRouterReducer,
} from '@flowx/designer/shared/store/reducers/stable-router.reducer'

import { AnalyticsModule } from './modules/analytics/analytics.module'
import { AnalyticsConfig } from './modules/analytics/analytics.service'
import { AuthConfigModule } from './modules/auth/auth.module'
import { CoreModule } from './modules/core/core.module'
import { LayoutModule } from './modules/layout/layout.module'

const FlxPreset = definePreset(Aura, {
  primitive: {
    blue: {
      50: '#e6f0fb',
      100: '#b0d1f3',
      200: '#8abbed',
      300: '#549ce5',
      400: '#3389e0',
      500: '#006bd8',
      600: '#005FC0',
      700: '#004c99',
      800: '#003b77',
      900: '#002d5b',
    },
    neutral: {
      50: '#f7f8f9',
      100: '#E3E8ED',
      200: '#CBD1DB',
      300: '#A6B0BE',
      400: '#8390a2',
      500: '#64748b',
      600: '#5b6a7e',
      700: '#475263',
      800: '#2A313A',
      900: '#1d232c',
    },
  },
  semantic: {
    primary: {
      50: '{blue.50}',
      100: '{blue.100}',
      200: '{blue.200}',
      300: '{blue.300}',
      400: '{blue.400}',
      500: '{blue.500}',
      600: '{blue.600}',
      700: '{blue.700}',
      800: '{blue.800}',
      900: '{blue.900}',
    },
  },
})

export function getBaseHref(platformLocation: PlatformLocation): string {
  return platformLocation.getBaseHrefFromDOM()
}

export const appConfig: ApplicationConfig = {
  providers: [
    // Angular core providers
    // Coalesce change detection across bubbling DOM events so a single user
    // interaction triggers one tick instead of one per event listener.
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideAnimationsAsync(),
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    providePrimeNG({
      theme: {
        preset: FlxPreset,
        options: { darkModeSelector: false },
      },
      ripple: true,
    }),
    // Give [formField] elements the ng-* status classes Reactive Forms add: PrimeNG's invalid
    // styling, our PrimeNG overrides and p-table's cell-edit guard all read them. Reactive Forms
    // controls are not affected; they get their classes from NgControlStatus.
    provideSignalFormsConfig({ classes: NG_STATUS_CLASSES }),

    // Module providers (NgRx must be initialized before feature modules)
    importProvidersFrom(
      AppRoutingModule,
      // NgRx store setup
      StoreModule.forRoot(
        { router: routerReducer, [stableRouterFeatureKey]: stableRouterReducer },
        {
          runtimeChecks: {
            strictStateImmutability: false,
            strictActionImmutability: false,
          },
        }
      ),
      EffectsModule.forRoot(fromStore.projectDataModelEffects, StableRouterEffects),
      StoreRouterConnectingModule.forRoot(),
      StoreModule.forFeature(dataModelFeatureKey, dataModelReducer),
      StoreModule.forFeature(reusableFunctionsFeatureKey, reusableFunctionsReducer),
      StoreDevtoolsModule.instrument({
        maxAge: 100,
        actionsBlocklist: ['aiConversationStreamUpdate'],
        connectInZone: true,
      }),

      // Feature modules
      CoreModule,
      LayoutModule,
      MonacoEditorModule.forRoot({ onMonacoLoad: monacoLoader }),
      AuthConfigModule,
      AnalyticsModule.forRoot(null),
      FlxIconModule
    ),
    provideEnvironmentInitializer(() => {
      inject(NotificationsSseService)
    }),
    // Services - TODO why are these not provided here?
    ReusableTemplatesService,
    UiFlowsService,
    MessageService,
    // Pipes
    FlxFileSizePipe,
    FlxTableDateFormatPipe,
    FlxDateFormatPipe,
    FlxHighlightPipe,

    // Injection tokens
    { provide: OverlayContainer, useClass: FlxOverlayContainer },
    // Angular CDK 21 renders overlays (flx-modal, etc.) in the browser top
    // layer via the Popover API (`.cdk-overlay-popover`). Top-layer elements
    // paint above all normal-flow content regardless of z-index, so PrimeNG
    // overlays appended to <body> (p-select, p-treeselect, …) render behind
    // CDK modals. Opt out of the top layer to restore z-index-based stacking.
    { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
    { provide: 'BASE_URL', useFactory: () => environment.baseUrl },
    { provide: 'STATIC_ASSETS_PATH', useFactory: () => environment.staticAssetsPath },
    { provide: 'PROCESS_API_PATH', useFactory: () => environment.processApiPath },
    { provide: 'LEGACY_HTTP_VERSION', useFactory: () => environment.legacyHttpVersion },
    { provide: 'SENTRY_DSN', useFactory: () => environment.sentryDsn },
    {
      provide: 'ANALYTICS_CONFIG',
      useFactory: () => ({
        token: (environment.analyticsConfig as AnalyticsConfig)?.token,
        trackPageViews: (environment.analyticsConfig as AnalyticsConfig)?.trackPageViews,
        distinct_id_field: (environment.analyticsConfig as AnalyticsConfig)?.distinct_id_field,
        anonymize: (environment.analyticsConfig as AnalyticsConfig)?.anonymize,
      }),
    },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    // What `ErrorInterceptor` does on a 401, made available to the streams it never sees.
    {
      provide: SESSION_LOGOUT,
      useFactory: (auth: AuthConfigService) => () => auth.logout(),
      deps: [AuthConfigService],
    },
    { provide: HTTP_INTERCEPTORS, useClass: WorkspacesInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AiAppIdInterceptor, multi: true },
    { provide: MEDIA_LIBRARY_API, useClass: MediaLibraryPrivateApi },
    MediaLibraryService,
    { provide: EFFECTOR_TOKEN, useValue: createDomain('BPMNEditorModule') },
    {
      provide: APP_BASE_HREF,
      useFactory: getBaseHref,
      deps: [PlatformLocation],
    },

    // Error handling & monitoring
    {
      provide: ErrorHandler,
      useClass: AnalyticsErrorHandler,
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },

    // Icons
    provideExtraIconSet({
      ...CMS_ICON_SET,
      ...DESIGNER_ICON_SET,
      ...ScriptingIcons,
    }),
  ],
}
